import clsx from "clsx";
import React, { useState } from "react";
import PropTypes from "prop-types";
import { ReactComponent as ErrorIcon } from "../../Assets/Icons/error_icon.svg";
import { ReactComponent as SuccessIcon } from "../../Assets/Icons/success_input_check.svg";
import { ReactComponent as HideIcon } from "../../Assets/Icons/hide.svg";
import { ReactComponent as ViewIcon } from "../../Assets/Icons/view.svg";
import ToggleComponent from "../ToggleComponent";

const Input = React.forwardRef(
  (
    {
      onBlur,
      label,
      type,
      onChange,
      value,
      name,
      autoComplete,
      error,
      readOnly = false,
      onFocus,
      focus,
      showError,
      success,
      disabled,
      required,
      placeholder,
      classes = "",
      info = [],
      variant,
      icon,
      outline,
      accept,
    },
    ref
  ) => {
    const commonClasses =
      "w-full h-10 rounded px-4 py-2 text-sm text-boudoir outline-none focus:outline-none";

    const normalInput = `border ${commonClasses}`;
    const dullClass = `bg-bleachedSilk border-none rounded-md ${commonClasses}`;

    const errorInput = `border-chestNut border focus:border-chestNut ${commonClasses}`;

    const successClass = `${commonClasses} border-jade focus:border-jade`;

    const [isPasswordShown, setIsPasswordShow] = useState(false);
    const togglePasswordType = () => setIsPasswordShow((prev) => !prev);

    return (
      <label className=" w-full font-sans" htmlFor={name}>
        <ToggleComponent check={label}>
          <span className="leading-6 font-semibold text-sm text-boudoir tracking-wider mb-2 inline-block">
            {label}
          </span>
        </ToggleComponent>
        <span className="relative block">
          {type !== "file" && (
            <input
              required={required}
              disabled={disabled}
              ref={ref}
              focus={focus ? "true" : undefined}
              id={name}
              autoComplete={autoComplete}
              name={name}
              readOnly={readOnly}
              value={value}
              onFocus={onFocus}
              onChange={onChange}
              type={type === "password" && isPasswordShown ? "text" : type}
              className={clsx({
                [normalInput]: !showError,
                [errorInput]: !success && showError,
                [successClass]: success,
                [dullClass]: variant === "dull",
                [classes]: true,
                "pl-10": !!icon,
                "border-seal": !outline,
                "border-t-0 border-b border-r-0 border-l-0 rounded-b-none border-moon": outline,
              })}
              onBlur={onBlur}
              placeholder={placeholder}
            />
          )}
          {type === "file" && (
            <input
              required={required}
              disabled={disabled}
              id={name}
              name={name}
              type='file'
              accept={accept}
              onChange={onChange}
              className={clsx({
                [normalInput]: !showError,
                [errorInput]: !success && showError,
                [successClass]: success,
                [dullClass]: variant === "dull",
                [classes]: true,
                "pl-10": !!icon,
                "border-seal": !outline,
                "border-t-0 border-b border-r-0 border-l-0 rounded-b-none border-moon": outline,
              })}
            />
          )}
          <ToggleComponent check={!!icon}>
            <span className=" absolute top-1/2 transform -translate-y-1/2 left-0 ml-4">
              {icon}
            </span>
          </ToggleComponent>
          <ToggleComponent check={!success && showError && type !== "password"}>
            <span className=" absolute top-1/2 transform -translate-y-1/2 right-0 mr-4">
              <ErrorIcon />
            </span>
          </ToggleComponent>
          <ToggleComponent check={type === "password"}>
            <button
              onClick={togglePasswordType}
              type="button"
              className="absolute top-1/2 transform -translate-y-1/2 right-0 mr-4 outline-none focus:outline-none"
            >
              {isPasswordShown ? <HideIcon /> : <ViewIcon />}
            </button>
          </ToggleComponent>
          <ToggleComponent check={success && type !== "password"}>
            <span className=" absolute top-1/2 transform -translate-y-1/2 right-0 mr-4">
              <SuccessIcon />
            </span>
          </ToggleComponent>
        </span>

        <span className="text-xs text-text-ruby">
          <ToggleComponent check={!success && showError}>
            <>
              {error &&
                error.map((errorValue) => (
                  <>
                    <span className="text-chestNut ">{errorValue}</span>

                    <br />
                  </>
                ))}
            </>
          </ToggleComponent>
        </span>
        {!showError && info && (
          <span className="text-xs text-text-dark">
            <>
              {info &&
                info.map((infoValue) => (
                  <>
                    <span className=" ">{infoValue}</span>

                    <br />
                  </>
                ))}
            </>
          </span>
        )}
      </label>
    );
  }
);

Input.defaultProps = {
  classes: "",
  label: "",
  type: "text",
  name: "",
  value: "",
  onFocus: () => {},
  onChange: () => {},
  onBlur: () => {},
  readOnly: false,
  autoComplete: "",
  focus: false,
  error: [],
  showError: false,
  success: false,
  disabled: false,
  required: false,
};

Input.propTypes = {
  label: PropTypes.string,
  type: PropTypes.string,
  value: PropTypes.string,
  onFocus: PropTypes.func,
  readOnly: PropTypes.bool,
  autoComplete: PropTypes.string,
  focus: PropTypes.bool,
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  name: PropTypes.string,
  error: PropTypes.oneOfType([PropTypes.array, PropTypes.string]),
  showError: PropTypes.bool,
  success: PropTypes.bool,
  disabled: PropTypes.bool,
  required: PropTypes.bool,
  classes: PropTypes.string,
};

export default Input;
